/**
 * Composes per-step mappers into the final API request shape.
 * Each step's mapping logic lives in its own file; this module
 * only orchestrates them.
 */
import type {
  ScheduleInterviewPayload,
  ScheduleMode,
  InterviewUpdateFlags,
} from '../config/types';
import { isSelfScheduleMode } from '../config/modeClassifiers';
import { convertToFormData } from '@/lib/util';
import type { ContextIds, ScheduleFeatureFlags } from './types';
import {
  stripUiOnlyFields,
  applyContextIdsForMode,
  mapDetailsToApi,
} from './mapDetails';
import { mapScorecardToApi } from './mapScorecard';
import { mapCommunicationToApi } from './mapCommunication';
import { mapSlotEmailToApi } from './mapSlotEmail';

export function buildPayload(
  payload: ScheduleInterviewPayload,
  contextIds: ContextIds,
  mode: ScheduleMode,
  flags?: InterviewUpdateFlags,
  featureFlags?: ScheduleFeatureFlags
): Record<string, unknown> {
  const details = stripUiOnlyFields(payload.details);
  const isSelfSchedule = isSelfScheduleMode(mode);

  return {
    ...applyContextIdsForMode(contextIds, mode),
    ...mapDetailsToApi(details, mode),
    ...mapCommunicationToApi(payload.communication, featureFlags),
    ...mapScorecardToApi(payload.scorecard),
    ...(isSelfSchedule ? mapSlotEmailToApi(payload.slotSelectionEmail) : {}),
    ...(flags ?? {}),
  };
}

/**
 * Same as `buildPayload` but serialised as `FormData` so per-audience
 * email configs can carry raw `File` blobs under `attachments[]`.
 * Use for /ats_interviews POST + /ats_schedules POST.
 */
export function buildPayloadFormData(
  payload: ScheduleInterviewPayload,
  contextIds: ContextIds,
  mode: ScheduleMode,
  flags?: InterviewUpdateFlags,
  featureFlags?: ScheduleFeatureFlags
): FormData {
  const jsonPayload = buildPayload(
    payload,
    contextIds,
    mode,
    flags,
    featureFlags
  );
  const formData = convertToFormData(jsonPayload);

  // `convertToFormData` drops empty arrays. The interview permit list
  // expects `participant_ids` even when the roster is empty.
  const participantIds = jsonPayload.participant_ids;
  if (Array.isArray(participantIds) && participantIds.length === 0) {
    formData.append('participant_ids[]', '');
  }

  return formData;
}
